import { ChangeDetectionStrategy, Component, computed, effect, inject, signal, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DeviceContextService } from '../device-context.service';
import { CloudApiService } from '../../../services/cloud-api.service';
import { linkedDeviceDisplayName, locationDisplayName, locationRefName } from '@/cloud/services/device-display';
import moment from 'moment';
import { Subscription } from 'rxjs';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { SkeletonModule } from 'primeng/skeleton';
import { MessageModule } from 'primeng/message';
import { Router, RouterLink } from '@angular/router';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { FormsModule } from '@angular/forms';
import { ContextMenuModule } from 'primeng/contextmenu';
import { LocationPickerComponent } from '../../../components/location-picker/location-picker.component';
import { MenuItem } from 'primeng/api';
import { ContextMenu } from 'primeng/contextmenu';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { SelectButtonModule } from 'primeng/selectbutton';
import { TooltipModule } from 'primeng/tooltip';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { SectionCardComponent } from '@/shared/ui/section-card/section-card.component';
import { EmptyStateComponent } from '@/shared/ui/empty-state/empty-state.component';
import { StatusBadgeComponent } from '@/shared/ui/status-badge/status-badge.component';
import { LinkedDeviceHealth, LinkedDeviceHealthMessage, deriveLinkedDeviceStatus, linkedDeviceProblem } from '@/cloud/models/device-status';

interface DeviceCapabilities {
    hasPower?: boolean;
    hasStandby?: boolean;
    hasInputControl?: boolean;
    hasCamera?: boolean;
    hasVolume?: boolean;
    hasMute?: boolean;
    hasHoist?: boolean;
}

interface DeviceState {
    powerStatus?: string;
    currentInput?: string;
    availableInputs?: string[];
    inStandby?: boolean;
}

interface LinkedDevice {
    id: string;
    name: string;
    type?: string;
    manufacturerName?: string;
    modelName?: string;
    serialNumber?: string;
    online: boolean;
    missing: boolean;
    lastSeenUtc?: string;
    allocatedRoomId?: string;
    locationNodeId?: string;
    locationName?: string;
    uniqueId?: string;
    runtimeDeviceId?: number;
    identity?: string;
    versionInfo?: string;
    connectionInfo?: string;
    capabilities?: DeviceCapabilities;
    state?: DeviceState;
    commands?: string[];
    /** Driver-reported health from the last check-in; absent before the processor reports it. */
    health?: LinkedDeviceHealth | null;
    messages?: LinkedDeviceHealthMessage[];
}

type StatusFilter = 'all' | 'attention' | 'online' | 'offline' | 'missing';
type ViewMode = 'card' | 'table';

interface CapabilityDef {
    key: keyof DeviceCapabilities;
    icon: string;
    label: string;
}

@Component({
    selector: 'app-linked-devices',
    standalone: true,
    imports: [
        CommonModule,
        StatusBadgeComponent,
        TableModule,
        TagModule,
        SkeletonModule,
        MessageModule,
        RouterLink,
        DialogModule,
        ButtonModule,
        FormsModule,
        ContextMenuModule,
        LocationPickerComponent,
        InputTextModule,
        SelectModule,
        SelectButtonModule,
        TooltipModule,
        IconFieldModule,
        InputIconModule,
        SectionCardComponent,
        EmptyStateComponent
    ],
    templateUrl: './linked-devices.html',
    styleUrl: './linked-devices.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class LinkedDevices implements OnDestroy {
    private ctx = inject(DeviceContextService);
    private api = inject(CloudApiService);
    private router = inject(Router);
    private syncSub: Subscription | null = null;
    private stateUpdateSub: Subscription | null = null;

    deviceSig = this.ctx.device;
    linkedDevices = signal<LinkedDevice[]>([]);
    loading = signal(true);
    error = signal<string | null>(null);

    // Search & filter
    searchQuery = signal('');
    statusFilter = signal<StatusFilter>('all');
    viewMode = signal<ViewMode>('card');

    viewModeOptions = [
        { label: '', value: 'card', icon: 'pi pi-th-large' },
        { label: '', value: 'table', icon: 'pi pi-list' }
    ];

    statusFilterOptions = [
        { label: 'All', value: 'all' },
        { label: 'Needs attention', value: 'attention' },
        { label: 'Online', value: 'online' },
        { label: 'Offline', value: 'offline' },
        { label: 'Missing', value: 'missing' }
    ];

    capabilityDefs: CapabilityDef[] = [
        { key: 'hasPower', icon: 'pi pi-power-off', label: 'Power' },
        { key: 'hasInputControl', icon: 'pi pi-desktop', label: 'Input Control' },
        { key: 'hasCamera', icon: 'pi pi-video', label: 'Camera' },
        { key: 'hasVolume', icon: 'pi pi-volume-up', label: 'Volume' },
        { key: 'hasMute', icon: 'pi pi-volume-off', label: 'Mute' },
        { key: 'hasStandby', icon: 'pi pi-moon', label: 'Standby' },
        { key: 'hasHoist', icon: 'pi pi-arrows-v', label: 'Hoist' }
    ];

    // Map of location IDs to location details
    locationMap = signal<Map<string, any>>(new Map());

    // Fetch linked devices whenever device changes, and subscribe to sync events
    private linkedDevicesEffect = effect(() => {
        const dev = this.deviceSig();
        const id = dev?.id;
        if (id) {
            this.loadLinkedDevices(id);
            this.subscribeToSync(id);
            this.subscribeToStateUpdates(id);
        } else {
            this.linkedDevices.set([]);
            this.loading.set(false);
            this.cleanupSubscriptions();
        }
    });

    filteredDevices = computed(() => {
        let devices = this.linkedDevices();
        const query = this.searchQuery().toLowerCase().trim();
        const status = this.statusFilter();

        if (status !== 'all') {
            devices = devices.filter(d => {
                if (status === 'missing') return d.missing;
                if (status === 'attention') return this.needsAttention(d);
                if (status === 'online') return d.online && !d.missing;
                if (status === 'offline') return !d.online && !d.missing;
                return true;
            });
        }

        if (query) {
            devices = devices.filter(d =>
                (d.name?.toLowerCase().includes(query)) ||
                (d.type?.toLowerCase().includes(query)) ||
                (d.manufacturerName?.toLowerCase().includes(query)) ||
                (d.modelName?.toLowerCase().includes(query)) ||
                (d.serialNumber?.toLowerCase().includes(query))
            );
        }

        return devices;
    });

    private subscribeToSync(parentDeviceId: string) {
        this.cleanupSyncSub();
        this.syncSub = this.api.subscribeToLinkedDeviceSync(parentDeviceId).subscribe(event => {
            if (event) {
                this.silentRefresh(parentDeviceId);
            }
        });
    }

    private subscribeToStateUpdates(parentDeviceId: string) {
        this.cleanupStateUpdateSub();
        this.stateUpdateSub = this.api.subscribeToLinkedDeviceStateUpdates(parentDeviceId).subscribe(update => {
            if (!update?.linkedDeviceId) return;
            const devices = this.linkedDevices();
            const idx = devices.findIndex(d => d.id === update.linkedDeviceId);
            if (idx >= 0) {
                const updated = { ...devices[idx] };
                if (update.state) updated.state = updated.state ? { ...updated.state, ...update.state } : update.state;
                if (update.online !== undefined) updated.online = update.online;
                const newList = [...devices];
                newList[idx] = updated;
                this.linkedDevices.set(newList);
            }
        });
    }

    private cleanupSyncSub() {
        if (this.syncSub) {
            this.syncSub.unsubscribe();
            this.syncSub = null;
        }
    }

    private cleanupStateUpdateSub() {
        if (this.stateUpdateSub) {
            this.stateUpdateSub.unsubscribe();
            this.stateUpdateSub = null;
        }
    }

    private cleanupSubscriptions() {
        this.cleanupSyncSub();
        this.cleanupStateUpdateSub();
    }

    ngOnDestroy() {
        this.cleanupSubscriptions();
    }

    async loadLinkedDevices(deviceId: string) {
        this.loading.set(true);
        this.error.set(null);
        try {
            const children = await this.api.getLinkedDevices(deviceId);
            this.linkedDevices.set(children || []);

            // Fetch location details for all unique room IDs
            await this.loadLocationDetails(children || []);
        } catch (err: any) {
            console.error('Failed to load linked devices', err);
            this.error.set(err?.message || 'Failed to load linked devices');
            this.linkedDevices.set([]);
        } finally {
            this.loading.set(false);
        }
    }

    /** Background refresh that merges new data without triggering loading state or page redraw */
    private async silentRefresh(deviceId: string) {
        try {
            const children: LinkedDevice[] = (await this.api.getLinkedDevices(deviceId)) || [];
            const current = this.linkedDevices();

            // Build a map of current devices by ID for fast lookup
            const currentMap = new Map(current.map(d => [d.id, d]));
            const newMap = new Map(children.map(d => [d.id, d]));

            // Detect if there's any meaningful change before updating
            const hasStructuralChange =
                current.length !== children.length ||
                children.some(d => !currentMap.has(d.id)) ||
                current.some(d => !newMap.has(d.id));

            if (hasStructuralChange) {
                // Devices added/removed — replace list
                this.linkedDevices.set(children);
            } else {
                // Same devices — merge updated fields in-place to avoid redraw
                let anyChange = false;
                const merged = current.map(existing => {
                    const fresh = newMap.get(existing.id);
                    if (!fresh) return existing;
                    // Check for actual field changes
                    if (
                        existing.online !== fresh.online ||
                        existing.missing !== fresh.missing ||
                        existing.lastSeenUtc !== fresh.lastSeenUtc ||
                        existing.state?.powerStatus !== fresh.state?.powerStatus ||
                        existing.state?.currentInput !== fresh.state?.currentInput ||
                        existing.state?.inStandby !== fresh.state?.inStandby ||
                        existing.locationNodeId !== fresh.locationNodeId
                    ) {
                        anyChange = true;
                        return { ...existing, ...fresh, state: fresh.state ? { ...(existing.state || {}), ...fresh.state } : existing.state };
                    }
                    return existing;
                });
                if (anyChange) {
                    this.linkedDevices.set(merged);
                }
            }
        } catch (err) {
            console.debug('Silent refresh failed', err);
        }
    }

    async loadLocationDetails(children: LinkedDevice[]) {
        const uniqueRoomIds = [...new Set(children
            .map(c => c.allocatedRoomId)
            .filter((id): id is string => !!id && id.trim() !== ''))];

        if (uniqueRoomIds.length === 0) return;

        const locationMap = new Map<string, any>();

        await Promise.all(uniqueRoomIds.map(async (roomId) => {
            try {
                const locations = await this.api.getLocations();
                const location = locations.find((l: any) => l.uniqueId === roomId);
                if (location) {
                    locationMap.set(roomId, location);
                }
            } catch (err) {
                console.debug('Failed to load location', roomId, err);
            }
        }));

        this.locationMap.set(locationMap);
    }

    getRoomInfo(allocatedRoomId: string | null | undefined) {
        if (!allocatedRoomId) return null;
        return this.locationMap().get(allocatedRoomId);
    }

    getOnlineCount = computed(() => {
        return this.linkedDevices().filter(d => d.online && !d.missing).length;
    });

    getOfflineCount = computed(() => {
        return this.linkedDevices().filter(d => !d.online && !d.missing).length;
    });

    getMissingCount = computed(() => {
        return this.linkedDevices().filter(d => d.missing).length;
    });

    /** Online devices whose driver reports warnings or errors. */
    getAttentionCount = computed(() => this.linkedDevices().filter(d => this.needsAttention(d)).length);

    linkedStatus = deriveLinkedDeviceStatus;
    linkedProblem = linkedDeviceProblem;

    private needsAttention(d: LinkedDevice): boolean {
        return d.online && !d.missing && (d.health === 'warning' || d.health === 'error');
    }

    formatLastSeen(child: LinkedDevice): string {
        if (child?.missing) return 'Missing';
        const ts = child?.lastSeenUtc;
        if (!ts) return 'Never seen';
        const m = moment.utc(ts);
        if (!m.isValid()) return 'Never seen';
        if (child?.online) return 'Online now';
        return m.fromNow();
    }

    /** Template helper — see linkedDeviceDisplayName for the precedence. */
    displayName = linkedDeviceDisplayName;

    getLocationName(locationId: string): string {
        const location = this.availableLocations().find(l => l.id === locationId);
        return locationDisplayName(location?.name, location?.type);
    }

    /**
     * The location a linked device sits in, never an id. `allocatedRoomId` is the processor's
     * own room GUID, which is meaningless to a reader, so it only decides whether a room is
     * claimed at all — the label itself comes from the synced location name.
     */
    locationLabel(device: { locationName?: string | null; locationNodeId?: string | null; allocatedRoomId?: string | null }): string {
        const locationId = device.locationNodeId ?? device.allocatedRoomId ?? null;
        return locationRefName(device.locationName, locationId, 'room');
    }

    getDeviceSubtitle(device: LinkedDevice): string {
        const parts: string[] = [];
        if (device.type) parts.push(device.type);
        if (device.manufacturerName && device.modelName) {
            parts.push(`${device.manufacturerName} ${device.modelName}`);
        } else if (device.manufacturerName) {
            parts.push(device.manufacturerName);
        } else if (device.modelName) {
            parts.push(device.modelName);
        }
        return parts.join(' · ');
    }

    getDeviceCapabilities(device: LinkedDevice): CapabilityDef[] {
        if (!device.capabilities) return [];
        return this.capabilityDefs.filter(cap => device.capabilities?.[cap.key]);
    }

    hasAnyCapability(device: LinkedDevice): boolean {
        return this.getDeviceCapabilities(device).length > 0;
    }

    getPowerStatusLabel(device: LinkedDevice): string {
        return device.state?.powerStatus || 'Unknown';
    }

    navigateToDetail(device: LinkedDevice) {
        if (device?.id) {
            this.router.navigate(['/linked-device', device.id]);
        }
    }

    // Context menu
    contextMenuItems: MenuItem[] = [];

    showContextMenu(event: MouseEvent, device: LinkedDevice, menu: ContextMenu) {
        event.preventDefault();
        event.stopPropagation();
        this.selectedLinkedDevice.set(device);
        this.contextMenuItems = [
            {
                label: 'Move to Location',
                icon: 'pi pi-arrow-right',
                command: () => this.openMoveDialog(this.selectedLinkedDevice())
            },
            ...(device.missing ? [{
                label: 'Remove Device',
                icon: 'pi pi-trash',
                command: () => this.openRemoveDialog(this.selectedLinkedDevice())
            }] : [])
        ];
        menu.show(event);
    }

    // Move child device dialog
    showMoveDialog = signal(false);
    selectedLinkedDevice = signal<LinkedDevice | null>(null);
    availableLocations = signal<any[]>([]);
    selectedTargetLocation = signal<string | null>(null);
    movingLinkedDeviceId = signal<string | null>(null);
    moveError = signal<string | null>(null);

    async openMoveDialog(linkedDevice: LinkedDevice | null) {
        if (!linkedDevice) return;
        this.selectedLinkedDevice.set(linkedDevice);
        this.selectedTargetLocation.set(null);
        this.moveError.set(null);

        try {
            const locations = await this.api.getLocations();
            const device = this.deviceSig();
            if (device?.appNamespaceId) {
                const filtered = locations.filter((l: any) => l.appNamespaceId === device.appNamespaceId);
                this.availableLocations.set(filtered);
            }
        } catch (err: any) {
            this.moveError.set('Failed to load locations');
            console.error('Failed to load locations', err);
        }

        this.showMoveDialog.set(true);
    }

    async moveLinkedDevice() {
        const linkedDevice = this.selectedLinkedDevice();
        const targetLocationId = this.selectedTargetLocation();

        if (!linkedDevice || !targetLocationId) {
            this.moveError.set('Please select a target location');
            return;
        }

        this.movingLinkedDeviceId.set(linkedDevice.id);
        this.moveError.set(null);

        try {
            await this.api.moveLinkedDevice(linkedDevice.id, targetLocationId);

            const linkedDevices = this.linkedDevices();
            const index = linkedDevices.findIndex(c => c.id === linkedDevice.id);
            if (index >= 0) {
                const updated = { ...linkedDevices[index], locationNodeId: targetLocationId };
                const newList = [...linkedDevices];
                newList[index] = updated;
                this.linkedDevices.set(newList);
            }

            this.showMoveDialog.set(false);
            this.selectedLinkedDevice.set(null);
        } catch (err: any) {
            this.moveError.set(err?.message || 'Failed to move child device');
            console.error('Failed to move child device', err);
        } finally {
            this.movingLinkedDeviceId.set(null);
        }
    }

    // Remove child device
    showRemoveDialog = signal(false);
    removeTarget = signal<LinkedDevice | null>(null);
    removeSubmitting = signal(false);
    removeError = signal<string | null>(null);

    openRemoveDialog(linkedDevice: LinkedDevice | null) {
        if (!linkedDevice) return;
        this.removeTarget.set(linkedDevice);
        this.removeError.set(null);
        this.showRemoveDialog.set(true);
    }

    async confirmRemoveLinkedDevice() {
        const linkedDevice = this.removeTarget();
        if (!linkedDevice?.id) return;

        this.removeSubmitting.set(true);
        this.removeError.set(null);

        try {
            await this.api.deleteLinkedDevice(linkedDevice.id);
            this.linkedDevices.set(this.linkedDevices().filter(c => c.id !== linkedDevice.id));
            this.showRemoveDialog.set(false);
            this.removeTarget.set(null);
        } catch (err: any) {
            console.error('Failed to remove child device', err);
            this.removeError.set(err?.error?.detail || err?.message || 'Failed to remove child device');
        } finally {
            this.removeSubmitting.set(false);
        }
    }
}
